import ExcelJS from 'exceljs';

export type CellValue = string | number | boolean | null | undefined;

export type ColumnRange = {
  start: number;
  end: number;
};
export const normalizeCellValue = (value: CellValue): CellValue => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber)) return asNumber;
  }
  return value;
};

export const applyHeaderStyle = (
  cell: ExcelJS.Cell,
  fillColor: string,
  options: { bold?: boolean } = {}
) => {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: fillColor },
  };
  cell.font = {
    bold: options.bold ?? true,
    name: 'Arial',
  };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
};

export const renderFieldHeaders = <T extends { label: string; subLabel: string }>(
  worksheet: ExcelJS.Worksheet,
  fields: T[],
  startColumn: number,
  getFillColor: (field: T) => string,
  options: {
    labelRow?: number;
    subLabelRow?: number;
    getSubLabel?: (field: T) => string;
  } = {}
) => {
  const labelRow = options.labelRow ?? 2;
  const subLabelRow = options.subLabelRow ?? 3;
  const getSubLabel = options.getSubLabel ?? ((field: T) => field.subLabel);

  fields.forEach((field, index) => {
    const columnIndex = startColumn + index;
    const fillColor = getFillColor(field);

    worksheet.getCell(labelRow, columnIndex).value = field.label;
    worksheet.getCell(subLabelRow, columnIndex).value = getSubLabel(field);

    applyHeaderStyle(worksheet.getCell(labelRow, columnIndex), fillColor);
    applyHeaderStyle(worksheet.getCell(subLabelRow, columnIndex), fillColor, { bold: false });
  });
};

export const mergeColumnsByPaintBlock = <T extends { paintRegionName: string }>(
  worksheet: ExcelJS.Worksheet,
  rows: T[],
  startRow: number,
  columnRanges: ColumnRange[]
) => {
  if (rows.length === 0) return;

  const shouldMerge = (value: string) => value.trim().length > 0;
  let currentValue = rows[0].paintRegionName;
  let blockStart = startRow;

  const finalizeBlock = (endRow: number) => {
    if (!shouldMerge(currentValue) || endRow <= blockStart) return;
    columnRanges.forEach(({ start, end }) => {
      if (start > end) return;
      for (let columnIndex = start; columnIndex <= end; columnIndex += 1) {
        worksheet.mergeCells(blockStart, columnIndex, endRow, columnIndex);
        const cell = worksheet.getCell(blockStart, columnIndex);
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        cell.font = { name: 'Arial' };
      }
    });
  };

  rows.forEach((row, index) => {
    if (index === 0) return;
    const rowValue = row.paintRegionName;
    const rowIndex = startRow + index;

    if (rowValue !== currentValue) {
      finalizeBlock(rowIndex - 1);
      currentValue = rowValue;
      blockStart = rowIndex;
    }
  });

  finalizeBlock(startRow + rows.length - 1);
};

export const getLastColumn = (startColumn: number, fieldCount: number) =>
  startColumn + fieldCount - 1;
